import { createContext, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { setHostKind, clearMasterSelection, setActiveStore, getActiveStore } from "@/lib/scope";
import { saveOfflineSession, readOfflineSession, clearOfflineSession } from '@/lib/offline-session';
import { networkFailure } from '@/lib/shift-journal';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [tenantError, setTenantError] = useState("");
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const expireSession = () => {
      localStorage.removeItem('pos_token'); clearOfflineSession();
      if (active) setUser(false);
    };
    window.addEventListener('pos-auth-expired', expireSession);
    const initialize = async () => {
      const token = localStorage.getItem('pos_token');
      try {
        const { data: host } = await api.get('/tenant', { timeout: 15000 });
        if (!active) return;
        setHostKind(host.kind); setTenant(host);
        if (!token) { setUser(false); return; }
        const { data: account } = await api.get('/auth/me', { timeout: 15000 });
        if (!active) return;
        if (host.kind === 'tenant') {
          // /stores is branch-scoped: replace the previous account's selection
          // using the verified identity before the first scoped request.
          if (account.store_id && account.branch_scope !== 'selected') setActiveStore(account.store_id);
          const { data: stores } = await api.get('/stores', { timeout: 15000 });
          if (!active) return;
          const preferred = (account.branch_scope === 'selected' && stores.some(s => s.id === getActiveStore()) ? getActiveStore() : '') || account.store_id || (stores.some(s => s.id === getActiveStore()) ? getActiveStore() : '') || stores.find(s => s.is_default)?.id || stores[0]?.id;
          if (preferred) setActiveStore(preferred);
          try { saveOfflineSession(host, account, stores, token); } catch { /* Online only if storage unavailable. */ }
        }
        setUser(account);
      } catch (e) {
        if (!active) return;
        const cached = networkFailure(e) ? readOfflineSession() : null;
        if (cached) { setHostKind(cached.tenant.kind); setTenant(cached.tenant); setUser(cached.user); }
        else if (e.response?.status === 401 || e.response?.status === 403) {
          localStorage.removeItem('pos_token'); clearOfflineSession(); setUser(false);
        } else {
          setTenantError('Tidak dapat membuka POS offline. Masuk online dahulu pada alamat dan perangkat ini; sesi offline harus masih berlaku.');
          setUser(false);
        }
      } finally { if (active) setLoading(false); }
    };
    initialize();
    return () => { active = false; window.removeEventListener('pos-auth-expired', expireSession); };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password }, { headers: { 'X-POS-Refresh': '1' } });
    clearMasterSelection();
    localStorage.setItem("pos_token", data.access_token);
    if (tenant?.kind === 'tenant') {
      if (data.user.store_id && data.user.branch_scope !== 'selected') setActiveStore(data.user.store_id);
      const { data: stores } = await api.get('/stores');
      const preferred = (data.user.branch_scope === 'selected' && stores.some(s => s.id === getActiveStore()) ? getActiveStore() : '') || data.user.store_id || (stores.some(s => s.id === getActiveStore()) ? getActiveStore() : '') || stores.find(s => s.is_default)?.id || stores[0]?.id;
      if (preferred) setActiveStore(preferred);
      try { saveOfflineSession(tenant, data.user, stores, data.access_token); } catch { /* online only */ }
    }
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    api.post('/auth/logout', {}, { headers: { 'X-POS-Refresh': '1' } }).catch(() => {});
    clearMasterSelection();
    localStorage.removeItem("pos_token");
    clearOfflineSession();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, apiError, tenant, tenantError, readOnly: tenant?.kind === "master" }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
